/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { View, Text, Actionable, Card, Carousel } from '@az/starc-ui';
import styles from './styles.module.scss';
import React, { useEffect, useState } from 'react';
import { ReviewImageDetailsModal } from '../ReviewImageDetailsModal/ReviewImageDetailsModal';
import NextImage from '@/components/NextImage';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { GalleryView } from '../GalleryView/GalleryView';
import { useLabels } from '@/hooks/useLabels';
import { type ReviewPhotoMap } from '@/features/customReviews/hooks/useProductReviewImages';
import { type ReviewType } from '../../../interface/types';
import { Review } from '../../Review/Review';
import { useBvPixelTracking } from '../../../hooks/useBvPixelTracking';

type ImageProps = {
  images: string[] | undefined;
  setSelectedImageIndex: React.Dispatch<React.SetStateAction<number>>;
  setDetailsModalState: React.Dispatch<React.SetStateAction<boolean>>;
  photoCaptions: string[] | undefined | null;
};

const Images = ({
  images,
  setSelectedImageIndex,
  setDetailsModalState,
  photoCaptions,
}: ImageProps) => {
  const previewImages = images?.slice(0, 5) ?? [];

  const { trackEvent } = useBvPixelTracking();

  return (
    <>
      {previewImages.map((image, index) => {
        return (
          <Actionable
            key={index}
            onClick={() => {
              setSelectedImageIndex(index);
              setDetailsModalState(true);
              trackEvent('Review Images', 'Click', 'Review Image');
            }}
          >
            <View.Item>
              <NextImage
                src={image}
                width={120}
                height={120}
                alt={photoCaptions?.[index] || 'Review Image' + `${index + 1}`}
                className={styles.images}
              />
            </View.Item>
          </Actionable>
        );
      })}
    </>
  );
};

export type PreviewImageProps = {
  imagesUrls: string[] | undefined;
  photoCaptions: string[] | undefined | null;
  reviews?: ReviewType[] | null | undefined | ReviewType;
  photoMap?: ReviewPhotoMap | null | undefined;
};

const labelsMap = {
  CustomerImages: 'label_Reviews_CustomerImages',
  seeAllImages: 'label_Reviews_See_All_Images',
};

export const PreviewImages = ({
  imagesUrls,
  photoCaptions,
  reviews,
  photoMap,
}: PreviewImageProps) => {
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const [isDetailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [images, setImages] = useState<string[] | undefined>(imagesUrls);

  const { trackEvent } = useBvPixelTracking();

  useEffect(() => {
    setImages(imagesUrls);
  }, [imagesUrls]);

  const labels = useLabels(labelsMap);

  const handleGalleryModalState: React.Dispatch<React.SetStateAction<boolean>> = (data) => {
    setIsGalleryModalOpen(data);
  };

  const handleDetailsModalState: React.Dispatch<React.SetStateAction<boolean>> = (data) => {
    setDetailsModalOpen(data);
  };
  const enableShowAllImages = (images?.length ?? 5) > 5;

  return (
    <View direction={'column'} className={styles.reviewImagesContainer}>
      <View direction={'row'} justify="space-between" className={styles.reviewImageHeader}>
        <View.Item>
          <Text className={styles.headerLeftText}>{labels.CustomerImages}</Text>
        </View.Item>
        {!isDesktop && enableShowAllImages ? (
          <View.Item>
            <Actionable
              onClick={() => {
                handleGalleryModalState(true);
                setSelectedImageIndex(0);
                trackEvent('Review Images', 'Click', 'See All Images');
              }}
            >
              <Text decoration="underline" className={styles.headerRightText}>
                {labels.seeAllImages}
              </Text>
            </Actionable>
          </View.Item>
        ) : null}
      </View>
      <View className={styles.responsiveContainer}>
        <View className={styles.mobileCarouselWrapper}>
          <Carousel className={styles.carousel} hideControls={true}>
            <View direction={'row'} justify="space-between" className={styles.imagesContainer}>
              <Images
                images={images}
                setSelectedImageIndex={setSelectedImageIndex}
                setDetailsModalState={handleDetailsModalState}
                photoCaptions={photoCaptions}
              />
            </View>
          </Carousel>
        </View>

        <View className={styles.desktopContentWrapper}>
          <View direction={'row'} justify="space-between" className={styles.imagesContainer}>
            <Images
              images={images}
              setSelectedImageIndex={setSelectedImageIndex}
              setDetailsModalState={handleDetailsModalState}
              photoCaptions={photoCaptions}
            />
            <div className={`${styles.seeAllImagesWrapper} ${styles.hidden}`}>
              {enableShowAllImages && (
                <Actionable
                  onClick={() => {
                    handleGalleryModalState(true);
                    trackEvent('Review Images', 'Click', 'See All Images');
                  }}
                >
                  <Card
                    width="120px"
                    height="120px"
                    hasHoverEffect={false}
                    className={styles.imagePlaceholderCard}
                  >
                    <Text className={styles.imagesPlaceHolder}>{labels.seeAllImages}</Text>
                  </Card>
                </Actionable>
              )}
            </div>
          </View>
        </View>
      </View>

      {isGalleryModalOpen && (
        <GalleryView
          isGalleryModalOpen={isGalleryModalOpen}
          isReviewDetailsModalOpen={isDetailsModalOpen}
          handleGalleryModalOpen={handleGalleryModalState}
          images={images}
          photoCaptions={photoCaptions}
          handleReviewDetailsModalOpen={handleDetailsModalState}
          imageIndex={selectedImageIndex}
          setImageIndex={setSelectedImageIndex}
          reviews={reviews}
          photoMap={photoMap}
        />
      )}
      {isDetailsModalOpen && (
        <ReviewImageDetailsModal
          isReviewDetailsModalOpen={isDetailsModalOpen}
          handleReviewDetailsModalOpen={handleDetailsModalState}
          images={images}
          photoCaptions={photoCaptions}
          imageIndex={selectedImageIndex}
          setImageIndex={setSelectedImageIndex}
          reviews={reviews}
          photoMap={photoMap}
          renderReview={(reviewData) => <Review review={reviewData} detailsModal={true} />}
        />
      )}
    </View>
  );
};
